import { useMemo } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Chip,
  Alert,
} from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { EmptyState } from '@/components/EmptyState';
import { getTeacherWorkloadDirect, type TeacherWorkloadData } from '@timetable/api';

interface WorkloadReportProps {
  academicYear: string | null;
  versionValidFrom: string | null;
}

export function WorkloadReport({ academicYear, versionValidFrom }: WorkloadReportProps) {
  const { data: workloadData, isLoading, error } = useQuery({
    queryKey: ['workload-report', academicYear, versionValidFrom],
    queryFn: () => getTeacherWorkloadDirect(
      academicYear ?? undefined,
      versionValidFrom ?? undefined
    ),
    enabled: !!academicYear && !!versionValidFrom,
  });

  const { chartData, stats } = useMemo(() => {
    if (!workloadData || workloadData.length === 0) {
      return { chartData: [], stats: { average: 0, max: 0, min: 0 } };
    }

    const totalPeriods = workloadData.map(t => t.total_periods);
    const average = totalPeriods.reduce((a, b) => a + b, 0) / totalPeriods.length;
    const max = Math.max(...totalPeriods);
    const min = Math.min(...totalPeriods);

    const chartData = workloadData.map(teacher => ({
      name: teacher.teacher_name,
      periods: teacher.total_periods,
      free: teacher.free_periods,
    }));

    return {
      chartData,
      stats: { average: Math.round(average * 10) / 10, max, min },
    };
  }, [workloadData]);

  const getWorkloadStatus = (periods: number) => {
    if (periods > stats.average * 1.2) {
      return { label: 'Overloaded', color: 'error' as const };
    } else if (periods < stats.average * 0.8) {
      return { label: 'Underutilized', color: 'warning' as const };
    }
    return { label: 'Balanced', color: 'success' as const };
  };

  const getBarColor = (periods: number) => {
    if (periods > stats.average * 1.2) return '#d32f2f'; // Red
    if (periods < stats.average * 0.8) return '#ed6c02'; // Orange
    return '#2e7d32'; // Green
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load workload report. Please try again.
      </Alert>
    );
  }

  if (!workloadData || workloadData.length === 0) {
    return (
      <EmptyState
        title="No Data Available"
        description="No teacher workload data found for the selected period."
      />
    );
  }

  return (
    <Box>
      {/* Summary Statistics */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Workload Summary
        </Typography>
        <Box sx={{ display: 'flex', gap: 4, mt: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Average Workload
            </Typography>
            <Typography variant="h4">{stats.average}</Typography>
            <Typography variant="caption" color="text.secondary">
              periods per teacher
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Maximum Workload
            </Typography>
            <Typography variant="h4">{stats.max}</Typography>
            <Typography variant="caption" color="text.secondary">
              periods
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Minimum Workload
            </Typography>
            <Typography variant="h4">{stats.min}</Typography>
            <Typography variant="caption" color="text.secondary">
              periods
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Bar Chart Visualization */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Workload Distribution
        </Typography>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
            <YAxis label={{ value: 'Number of Periods', angle: -90, position: 'insideLeft' }} />
            <Tooltip />
            <Legend />
            <Bar dataKey="periods" name="Assigned Periods">
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getBarColor(entry.periods)} />
              ))}
            </Bar>
            <Bar dataKey="free" name="Free Periods" fill="#82ca9d" />
          </BarChart>
        </ResponsiveContainer>
      </Paper>

      {/* Detailed Table */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Teacher Workload Details
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Teacher Name</TableCell>
                <TableCell align="right">Total Periods</TableCell>
                <TableCell align="right">Free Periods</TableCell>
                <TableCell align="center">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {workloadData.map((teacher) => {
                const status = getWorkloadStatus(teacher.total_periods);
                return (
                  <TableRow key={teacher.teacher_id}>
                    <TableCell>{teacher.teacher_name}</TableCell>
                    <TableCell align="right">{teacher.total_periods}</TableCell>
                    <TableCell align="right">{teacher.free_periods}</TableCell>
                    <TableCell align="center">
                      <Chip label={status.label} color={status.color} size="small" />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
